import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RequestError } from '../../../interfaces/error.interface';
import { GetSubscriptionsParams, GetSubscriptionsResponse } from '../interfaces/get-subscriptions';
import { Subscription } from '../../../interfaces/subscription.interface';
import { createEntityAdapter } from '../../../helpers/create-entity-adapter';
import { PostContactResponse } from '../interfaces/post-contact';

export interface SubscriptionState {
  subscriptions: {
    ids: string[];
    entities: Record<string, Subscription>;
  };
  columnsVisibility: Record<string, boolean>,
  sorting: {
    column: string;
    order: 'asc' | 'desc';
  };
  subscription: Subscription | null;
  options: GetSubscriptionsParams;
  page: number;
  total: number;
  noMore: boolean;
  errorCode: number | null;
}

const initialState: SubscriptionState = {
  subscriptions: {
    ids: [],
    entities: {},
  },
  columnsVisibility: {
    subscriptionId: false,
    status: true,
    type: false,
    platform: true,
    emailSent: true,
    template: true,
    whatsappVisited: true,
    contactCreated: true,
    login: true,
    createdAt: true,
    expiresdAt: true,
    canceledAt: true,
  },
  sorting: {
    column: 'createdAt',
    order: 'desc',
  },
  subscription: null,
  options: {},
  page: 1,
  total: 0,
  noMore: false,
  errorCode: null,
};

const subscriptionAdapter = createEntityAdapter<Subscription>((subscription: Subscription) => subscription.id);

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    getSubscriptionsInitApiAction: (state) => {
      state.errorCode = null;
    },
    getSubscriptionsSuccessApiAction: (state, action: PayloadAction<GetSubscriptionsResponse>) => {
      subscriptionAdapter.updateMany(action.payload.subscriptions, state.subscriptions);
      state.total = action.payload.total;
      state.noMore = action.payload.subscriptions.length < 50;
    },
    getSubscriptionsFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postContactInitApiAction: (state) => {
      state.errorCode = null;
    },
    postContactSuccessApiAction: (state, action: PayloadAction<PostContactResponse>) => {
      subscriptionAdapter.updateOne(action.payload.subscription, state.subscriptions);
    },
    postContactFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postEmailInitApiAction: (state) => {
      state.errorCode = null;
    },
    postEmailSuccessApiAction: (state, action: PayloadAction<PostContactResponse>) => {
      subscriptionAdapter.updateOne(action.payload.subscription, state.subscriptions);
    },
    postEmailFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postSubscriptionInitApiAction: (state) => {
      state.errorCode = null;
    },
    postSubscriptionSuccessApiAction: () => {},
    postSubscriptionFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    putSubscriptionInitApiAction: (state) => {
      state.errorCode = null;
    },
    putSubscriptionSuccessApiAction: (state, action: PayloadAction<{ subscription: Subscription }>) => {
      const subscription = state.subscriptions.entities[action.payload.subscription.id];
      subscriptionAdapter.updateOne(
        { ...subscription, createdAt: action.payload.subscription.createdAt },
        state.subscriptions
      );
    },
    putSubscriptionFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getDownloadFileInitApiAction: (state) => {
      state.errorCode = null;
    },
    getDownloadFileSuccessApiAction: () => {},
    getDownloadFileFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    deleteSubscriptionInitApiAction: (state) => {
      state.errorCode = null;
    },
    deleteSubscriptionSuccessApiAction: (state, action: PayloadAction<{ subscription: Subscription }>) => {
      const subscription = state.subscriptions.entities[action.payload.subscription.id];
      subscriptionAdapter.updateOne(
        { ...subscription, status: action.payload.subscription.status, canceledAt: action.payload.subscription.canceledAt },
        state.subscriptions
      );
    },
    deleteSubscriptionFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    setOptionsAction: (state, action: PayloadAction<GetSubscriptionsParams>) => {
      subscriptionAdapter.removeAll(state.subscriptions);
      state.options = action.payload;
      state.page = 1;
    },
    setSubscriptionAction: (state, action: PayloadAction<Subscription | null>) => {
      state.subscription = action.payload;
    },
    increasePageAction: (state) => {
      state.page += 1;
    },
    resetOptionsAction: (state) => {
      state.options = {};
    },
    resetPageAction: (state) => {
      state.page = 1;
    },
    toggleVisibilityAction: (state, action: PayloadAction<{ id: string; visible: boolean }>) => {
      state.columnsVisibility = {
        ...state.columnsVisibility,
        [action.payload.id]: action.payload.visible,
      };
    },
    toggleSortingAction: (state, action: PayloadAction<{ id: string }>) => {
      if (state.sorting.column === action.payload.id) {
        if (state.sorting.order === 'asc') {
          state.sorting.order = 'desc';
        } else {
          state.sorting.order = 'asc';
        }
      } else {
        state.sorting = {
          column: action.payload.id,
          order: 'asc'
        };
      }
      state.page = 1;
      subscriptionAdapter.removeAll(state.subscriptions);
    },
    subscriptionClearAction: (state) => {
      subscriptionAdapter.removeAll(state.subscriptions);
      state.options = {};
      state.page = 1;
      state.total = 0;
      state.noMore = false;
    },
  },
});

export const {
  getSubscriptionsInitApiAction,
  getSubscriptionsSuccessApiAction,
  getSubscriptionsFailureApiAction,
  postContactInitApiAction,
  postContactSuccessApiAction,
  postContactFailureApiAction,
  postEmailInitApiAction,
  postEmailSuccessApiAction,
  postEmailFailureApiAction,
  postSubscriptionInitApiAction,
  postSubscriptionSuccessApiAction,
  postSubscriptionFailureApiAction,
  getDownloadFileInitApiAction,
  getDownloadFileSuccessApiAction,
  getDownloadFileFailureApiAction,
  deleteSubscriptionInitApiAction,
  deleteSubscriptionSuccessApiAction,
  deleteSubscriptionFailureApiAction,
  putSubscriptionInitApiAction,
  putSubscriptionSuccessApiAction,
  putSubscriptionFailureApiAction,
  setSubscriptionAction,
  setOptionsAction,
  increasePageAction,
  resetOptionsAction,
  resetPageAction,
  toggleVisibilityAction,
  toggleSortingAction,
  subscriptionClearAction,
} = subscriptionSlice.actions;

export default subscriptionSlice.reducer;
